import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { AuthForm, type AuthView } from '@/components/auth/auth-form'
import { callEdgeFunction } from '@/lib/edge-functions'
import { toast } from 'sonner'

interface AuthOverlayProps {
  isAuthenticated: boolean
}

interface ViewOverride {
  baseView: AuthView | null
  view: AuthView
}

const AUTH_VIEWS: AuthView[] = ['signin', 'signup', 'forgot']

function parseAuthView(value: string | null): AuthView | null {
  if (!value) {
    return null
  }

  if (AUTH_VIEWS.includes(value as AuthView)) {
    return value as AuthView
  }

  return null
}

function buildHref(pathname: string, params: URLSearchParams) {
  const query = params.toString()
  return query ? `${pathname}?${query}` : pathname
}

function getOverlayTitle(view: AuthView) {
  if (view === 'signup') {
    return 'Create an account'
  }

  if (view === 'forgot') {
    return 'Reset your password'
  }

  return 'Sign in to your account'
}

export function AuthOverlay({ isAuthenticated }: AuthOverlayProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const [isMobile, setIsMobile] = useState(false)
  const [viewOverride, setViewOverride] = useState<ViewOverride | null>(null)
  const inviteProcessingRef = useRef(false)

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 767px)')

    const handleChange = () => {
      setIsMobile(mediaQuery.matches)
    }

    handleChange()
    mediaQuery.addEventListener('change', handleChange)

    return () => {
      mediaQuery.removeEventListener('change', handleChange)
    }
  }, [])

  const authView = useMemo(() => parseAuthView(searchParams.get('auth')), [searchParams])
  const redirectTo = searchParams.get('redirect') || '/'
  const inviteToken = searchParams.get('invite')
  const inviteEmail = searchParams.get('invite_email')
  const authError = searchParams.get('error')

  // If invite token is present but no auth view, treat as signup
  const effectiveAuthView = authView || (inviteToken ? 'signup' : null)

  const currentView =
    viewOverride && viewOverride.baseView === effectiveAuthView
      ? viewOverride.view
      : effectiveAuthView
  const overlayTitle = currentView ? getOverlayTitle(currentView) : 'Authentication'

  const updateAuthRoute = (nextView: AuthView | null) => {
    const params = new URLSearchParams(searchParams.toString())

    if (nextView) {
      params.set('auth', nextView)
    } else {
      params.delete('auth')
      params.delete('error')
      params.delete('redirect')
      params.delete('invite')
      params.delete('invite_email')
    }

    const href = buildHref(location.pathname, params)
    navigate(href, { replace: true })
  }

  // Auto-accept invite when already authenticated
  useEffect(() => {
    if (isAuthenticated && inviteToken && !inviteProcessingRef.current) {
      inviteProcessingRef.current = true

      callEdgeFunction('accept-invite', {
        method: 'POST',
        body: { token: inviteToken },
        withAuth: true,
      })
        .then((result) => {
          // Clean up invite params from URL
          const params = new URLSearchParams(searchParams.toString())
          params.delete('auth')
          params.delete('error')
          params.delete('redirect')
          params.delete('invite')
          params.delete('invite_email')

          if (!result.error) {
            toast.success((result.data as { message?: string })?.message || 'Invitation accepted!')
            navigate('/admin', { replace: true })
          } else {
            toast.error(result.error)
            navigate(buildHref(location.pathname, params), { replace: true })
          }
        })
        .catch(() => {
          toast.error('Failed to accept invitation')
          const params = new URLSearchParams(searchParams.toString())
          params.delete('invite')
          params.delete('invite_email')
          params.delete('auth')
          navigate(buildHref(location.pathname, params), { replace: true })
        })
        .finally(() => {
          inviteProcessingRef.current = false
        })

      return
    }

    // Normal auth cleanup (no invite token)
    if (isAuthenticated && authView && !inviteToken) {
      const params = new URLSearchParams(searchParams.toString())
      params.delete('auth')
      params.delete('error')
      params.delete('redirect')
      navigate(buildHref(location.pathname, params), { replace: true })
    }
  }, [authView, inviteToken, isAuthenticated, location.pathname, navigate, searchParams])

  // Don't render overlay if authenticated (invite auto-processing handles the rest)
  if (isAuthenticated) {
    return null
  }

  if (!currentView) {
    return null
  }

  const handleViewChange = (nextView: AuthView) => {
    setViewOverride({
      baseView: effectiveAuthView,
      view: nextView,
    })
  }

  const handleSuccess = () => {
    updateAuthRoute(null)
    setViewOverride(null)
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      updateAuthRoute(null)
      setViewOverride(null)
    }
  }

  const form = (
    <AuthForm
      initialView={currentView}
      redirectTo={redirectTo}
      inviteToken={inviteToken}
      inviteEmail={inviteEmail}
      authError={authError}
      onViewChange={handleViewChange}
      onSuccess={handleSuccess}
    />
  )

  if (isMobile) {
    return (
      <Sheet open onOpenChange={handleOpenChange}>
        <SheetContent
          side="bottom"
          className="max-h-[92vh] overflow-y-auto rounded-t-2xl border-x-0 border-b-0 bg-muted/30 p-3"
        >
          <SheetTitle className="sr-only">{overlayTitle}</SheetTitle>
          {form}
        </SheetContent>
      </Sheet>
    )
  }

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogContent className="w-full max-w-md sm:max-w-md border-none bg-transparent p-0 shadow-none">
        <DialogTitle className="sr-only">{overlayTitle}</DialogTitle>
        {form}
      </DialogContent>
    </Dialog>
  )
}
